import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet-search';

import { Feature, FeatureCollection } from '../../models';

import * as ClusterFactory from './cluster/cluster.factory';
import * as IconFactory from '../../services/icon.factory';
import * as ImageFactory from './image/image.factory';
import * as MarkerHandler from './marker/marker.handler';

@Component({
    selector: 'kp-map-features',
    templateUrl: './kp-map-features.component.html',
    styleUrls: ['./kp-map-features.component.scss'],
    standalone: false,
})
export class KpFeaturesComponent implements OnChanges {
    @Input() data: FeatureCollection;
    @Input() map: L.Map;
    @Input() isEditing = false;
    @Input() layersControl: L.Control.Layers;
    @Input() showSearch = true;

    @Output() editImage: EventEmitter<Feature> = new EventEmitter();
    @Output() mutation: EventEmitter<FeatureCollection> = new EventEmitter();
    @Output() featureClicked = new EventEmitter<Feature>();

    private geoJSON: L.GeoJSON;
    private searchControl: L.Control;

    private clusters: L.FeatureGroup;
    // todo: fix
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore / not a typed fn, see https://github.com/Leaflet/Leaflet.markercluster/blob/master/src/MarkerClusterGroup.js
    private clusterableFeatures: L.FeatureGroup = L.markerClusterGroup();
    private editFeatures: L.FeatureGroup = L.featureGroup();
    private imageFeatures: L.FeatureGroup = L.featureGroup();
    private markers: L.Marker[] = [];

    private oldLatLng: L.LatLng;

    private isSearchExpanded = false;

    ngOnChanges(changes: SimpleChanges): void {
        if (this.map && changes.data && !this.isSearchExpanded) {
            this.clear();
            this.load(this.data);
        } else if (changes.isEditing) {
            this.setLayersEditableStatus();
        }
    }

    getBounds(): L.LatLngBounds {
        return this.imageFeatures.getBounds().extend(this.clusters.getBounds().pad(0.2));
    }

    load(data: FeatureCollection) {
        const map = this.map;
        if (!map) {
            throw new Error('A map has not been created yet. Can not load GeoJSON.');
        }
        if (!data) {
            return this.clear();
        }

        // Setup an empty Cluster Group
        this.clusters = ClusterFactory.emptyClusterGroup();

        // Load the GeoJSON
        this.geoJSON = L.geoJSON(data, {
            // Extract images and locations
            onEachFeature: (feature: Feature, layer: L.Layer) => {
                if (
                    (feature.properties?.type === 'image' || feature.properties?.type === 'location') &&
                    feature.properties.located
                ) {
                    const { overlay, imgRect, name, handles } = ImageFactory.imageFromFeature(
                        feature,
                        () => {
                            // Image has run-time elements.
                            // We need to merge these back to the GeoJSON before emitting mutations:
                            this.geoJSON = L.geoJSON(data);
                            this.mutate();
                        },
                        () => {
                            this.editImage.emit(feature);
                        }
                    );

                    // Add UI layers from the editable image components
                    this.clusterableFeatures.addLayer(name);
                    this.imageFeatures.addLayer(overlay);
                    this.editFeatures.addLayer(imgRect);
                    for (const handle of handles) {
                        this.editFeatures.addLayer(handle);
                    }

                    // Leaflet will add the GeoJSON polyline to the map, dim it.
                    // (We need it, do not delete.)
                    (layer as L.Polyline).setStyle({ opacity: 0 });
                }
            },
            // Make a marker for each point found, attach handlers
            pointToLayer: (feature, latlng) => {
                if (!feature.properties.located) {
                    return null;
                }
                const marker = L.marker(latlng, {
                    icon: IconFactory.leafletIconFromFeature(feature as Feature),
                    draggable: false,
                })
                    .on('mousedown', (ev: L.LeafletMouseEvent) => {
                        MarkerHandler.setSelected(ev.target.feature.properties.id, this.markers);
                    })
                    .on('mouseup', () => {
                        MarkerHandler.setSelected(null, this.markers);
                    })
                    .on('click', (ev: L.LeafletMouseEvent) => {
                        const feature: Feature = ev.target.feature;
                        if (feature) {
                            this.featureClicked.emit(feature);
                        }
                    })
                    .on('dragstart' as any, () => {
                        this.oldLatLng = undefined;
                    })
                    .on('dragend' as any, () => {
                        this.oldLatLng = undefined;
                        MarkerHandler.setSelected(null, this.markers);
                        this.mutate();
                    })
                    .on('drag' as any, (ev: L.LeafletMouseEvent) => {
                        const oldPos = this.oldLatLng || ev.latlng;
                        const diff = L.latLng({
                            lat: ev.latlng.lat - oldPos.lat,
                            lng: ev.latlng.lng - oldPos.lng,
                        });
                        MarkerHandler.moveChildMarkers(ev.target.feature.properties.id, this.markers, diff);
                        this.oldLatLng = ev.latlng;
                    });
                this.clusterableFeatures.addLayer(marker);
                this.markers.push(marker);
                return marker;
            },
        });

        if (this.showSearch) {
            this.setupSearch();
        }

        // Populate Clusters
        this.clusters.addLayer(this.clusterableFeatures);

        // Replace GeoJSON layer 'markers' with the new clustered layer
        map.removeLayer(this.geoJSON);
        map.addLayer(this.clusters);

        // Add images to the map
        map.addLayer(this.imageFeatures);
        // Images can be toggled in the Layers Control
        this.layersControl.addOverlay(this.imageFeatures, 'Background');
        // map.removeControl(this.layersControl);

        // Set initial status for edit UI elements
        this.setLayersEditableStatus();
    }

    // RESET MAP
    clear() {
        this.destroyLayerGroup(this.clusterableFeatures);
        this.destroyLayerGroup(this.editFeatures);
        this.destroyLayerGroup(this.imageFeatures);
        this.destroyLayerGroup(this.clusters);
        if (this.geoJSON) {
            this.geoJSON.clearLayers();
            this.geoJSON.off();
            this.geoJSON.remove();
        }
        this.layersControl.removeLayer(this.imageFeatures);
        this.markers = [];
    }

    // Emit mutations during editing
    mutate() {
        this.mutation.emit(this.geoJSON.toGeoJSON() as FeatureCollection);
    }

    // Clean up a Leaflet LayerGroup
    private destroyLayerGroup(lg: L.LayerGroup) {
        if (!lg) {
            return;
        }
        const layers = lg.getLayers();
        lg.off();
        lg.clearAllEventListeners();
        lg.clearLayers();
        for (const l of layers) {
            l.clearAllEventListeners();
            l.remove();
        }
        lg.remove();
    }

    // Toggle visibility of layers that have UI elements for editing (handles etc.)
    private setLayersEditableStatus() {
        if (this.isEditing) {
            for (const layer of this.editFeatures.getLayers()) {
                this.map.addLayer(layer);
            }
            for (const layer of this.clusterableFeatures.getLayers()) {
                if ((layer as L.Marker).dragging) {
                    (layer as L.Marker).dragging.enable();
                }
            }
        } else {
            for (const layer of this.editFeatures.getLayers()) {
                this.map.removeLayer(layer);
            }
            for (const layer of this.clusterableFeatures.getLayers()) {
                if ((layer as L.Marker).dragging) {
                    (layer as L.Marker).dragging.disable();
                }
            }
        }
    }

    private setupSearch() {
        // todo: fix
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        if (this.searchControl) {
            this.map.removeControl(this.searchControl);
        }
        // todo: fix
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore - the Search plugin is not typed
        this.searchControl = new L.Control.Search({
            position: 'topleft',
            layer: this.geoJSON,
            // sourceData: (text, callresponse) => {
            //     callresponse([
            //         { title: 'asd', loc: [0, 0] }
            //     ]);
            // },
            initial: false,
            propertyName: 'name',
            hideMarkerOnCollapse: true,
            autoCollapse: false,
            zoom: 18,
        })
            .on('search:expanded', function () {
                self.isSearchExpanded = true; // prevent loading new data  / removing search when user is typing
            })
            .on('search:collapsed', function () {
                self.isSearchExpanded = false;
            });

        this.map.addControl(this.searchControl);
    }
}
